import { Component, OnInit, Output, EventEmitter, Input } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { CdkDragDrop, moveItemInArray, DragDropModule } from '@angular/cdk/drag-drop';
import { VisionboardService } from '../../../../core/services/visionboard.service';
import { AuthService } from '../../../../core/services/auth.service';
import { Visionboard, VisionboardDto, VisionboardImage } from '../../../../core/models/visionboard.interface';
import { v4 as uuidv4 } from 'uuid';
import { SimpleChanges } from '@angular/core';
import { BaseComponent } from '../../../../core/shared/base.component';

@Component({
  selector: 'app-visionboard-editor',
  standalone: true,
  imports: [CommonModule, FormsModule, DragDropModule],
  templateUrl: './visionboard-editor.component.html',
  styleUrls: ['./visionboard-editor.component.scss']
})
export class VisionboardEditorComponent extends BaseComponent implements OnInit {
  @Input() visionboardToEditId: string | null = null;
  @Output() visionboardSaved = new EventEmitter<void>();

  visionboardName: string = '';
  visionboardDescription: string = '';
  images: { imageId: string, preview: string }[] = [];
  imageFiles: { id: string, file: File | null, preview: string }[] = [];
  visionboardId: string | null = null;
  userId: string = '';
  isLoading: boolean = false;
  userVisionboardImages: VisionboardImage[] = [];

  constructor(
    private visionboardService: VisionboardService,
    private authService: AuthService,
    private route: ActivatedRoute,
    private router: Router
  ) {
    super();
    const authUser = this.authService.getAuthUser();
    if (authUser && authUser.id) {
      this.userId = authUser.id;
    } else {
      console.error('VisionboardEditor: Usuario no autenticado.');
      this.router.navigate(['/auth/login']);
    }
  }

  ngOnInit(): void {
    if (!this.userId) return;

    this.loadUserImages();

    if (this.visionboardToEditId && this.visionboardToEditId !== 'new') {
      this.visionboardId = this.visionboardToEditId;
      this.loadVisionboard();
    } else {
      this.route.queryParams.subscribe(params => {
        if (params['id'] && params['id'] !== 'new') {
          this.visionboardId = params['id'];
          this.loadVisionboard();
        } else {
          this.resetEditor();
        }
      });
    }
  }

  loadUserImages(): void {
    if (!this.userId) return;
    this.isLoading = true;
    this.authService.findUserById(this.userId).subscribe({
      next: (response) => {
        if (response.data && response.data.visionboardImages) {
          this.userVisionboardImages = response.data.visionboardImages;
        }
        this.isLoading = false;
      },
      error: (err) => {
        console.error('Error al cargar imágenes del usuario:', err);
        this.isLoading = false;
      }
    });
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['visionboardToEditId']) {
      const newId = changes['visionboardToEditId'].currentValue;
      if (newId && newId !== 'new') {
        this.visionboardId = newId;
        this.loadVisionboard();
      } else {
        this.resetEditor();
      }
    }
  }

  resetEditor(): void {
    this.visionboardId = null;
    this.visionboardName = '';
    this.visionboardDescription = '';
    this.images = [];
    this.imageFiles = [];
  }

  loadVisionboard(): void {
    if (!this.userId || !this.visionboardId) return;

    this.isLoading = true;

    // Cargar primero las imágenes del usuario si no se han cargado ya
    if (this.userVisionboardImages.length === 0) {
      this.authService.findUserById(this.userId).subscribe({
        next: (userResponse) => {
          if (userResponse.data && userResponse.data.visionboardImages) {
            this.userVisionboardImages = userResponse.data.visionboardImages;
            this.loadVisionboardData();
          } else {
            this.loadVisionboardData();
          }
        },
        error: (err) => {
          console.error('Error al cargar imágenes del usuario:', err);
          this.isLoading = false;
        }
      });
    } else {
      this.loadVisionboardData();
    }
  }

  loadVisionboardData(): void {
    if (!this.visionboardId) {
      this.isLoading = false;
      return;
    }

    this.visionboardService.getUserVisionboards(this.userId).subscribe({
      next: (response) => {
        const visionboards = response.data;
        const visionboard = visionboards.find(vb => vb.id === this.visionboardId);
        if (visionboard) {
          this.visionboardName = visionboard.name;
          this.visionboardDescription = visionboard.description || '';

          this.images = [];
          this.imageFiles = [];

          if (visionboard.images && visionboard.images.length > 0) {
            visionboard.images.forEach(img => {
              const imageData = this.findImageData(img.imageId || '');
              if (imageData) {
                this.images.push({
                  imageId: img.imageId || '',
                  preview: imageData
                });

                // Agregar a imageFiles para gestionar la edición correctamente
                this.imageFiles.push({
                  id: img.imageId || '',
                  file: null, // No hay un archivo, es una imagen existente
                  preview: imageData
                });
              }
            });
          }
        }
        this.isLoading = false;
      },
      error: (error) => {
        console.error('Error al cargar visionboard', error);
        this.isLoading = false;
      }
    });
  }

  findImageData(imageId: string): string | null {
    if (!imageId) return null;

    // Buscar en archivos nuevos
    const newImage = this.imageFiles.find(imgFile => imgFile.id === imageId);
    if (newImage && newImage.preview) {
      return newImage.preview;
    }

    // Buscar en imágenes del usuario
    const userImage = this.userVisionboardImages.find(img => img.id === imageId);
    return userImage ? userImage.imageData : null;
  }

  async uploadImages(): Promise<void> {
    if (!this.userId) return;

    // Comprobar primero si ya hemos alcanzado el límite de 5 imágenes
    if (this.images.length >= 5) {
      // alert('Ya tienes 5 imágenes. Debes eliminar alguna antes de añadir más.');
      this.info('Ya tienes 5 imágenes. Debes eliminar alguna antes de añadir más.', '', 'Aceptar', true);
      return;
    }

    const fileInput = document.createElement('input');
    fileInput.type = 'file';
    fileInput.accept = 'image/*';
    fileInput.multiple = true;

    fileInput.onchange = async (event: any) => {
      const files = event.target.files;
      if (files && files.length) {
        const remainingSlots = 5 - this.images.length;
        const filesToProcess = Math.min(files.length, remainingSlots);

        if (files.length > remainingSlots) {
          // alert(`Solo puedes agregar ${remainingSlots} imagen(es) más. Se procesarán las primeras ${remainingSlots}.`);
          this.info(`Solo puedes agregar ${remainingSlots} imagen(es) más. Se procesarán las primeras ${remainingSlots}.`, '', 'Aceptar', true);
        }

        for (let i = 0; i < filesToProcess; i++) {
          const file = files[i];
          const id = uuidv4();

          try {
            const preview = await this.visionboardService.fileToBase64(file);

            this.imageFiles.push({ id, file, preview });
            this.images.push({
              imageId: id,
              preview,
            });
          } catch (error) {
            console.error('Error al convertir imagen', error);
          }
        }
      }
    };

    fileInput.click();
  }

  async saveVisionboard(): Promise<void> {

    if (!this.userId) {
      // alert('Error: No se pudo identificar al usuario. Por favor, inicia sesión nuevamente.');
      this.info('No se pudo identificar al usuario. Por favor, inicia sesión nuevamente.', '', 'Aceptar', true);
      this.router.navigate(['/auth/login']);
      return;
    }
    if (!this.visionboardName.trim()) {
      // alert('Por favor, ingresa un nombre para tu visionboard');
      this.info('Por favor, ingresa un nombre para tu visionboard', '', 'Aceptar', true);
      return;
    }

    // Verificar límite de imágenes
    if (this.images.length > 5) {
      // alert('Solo puedes guardar hasta 5 imágenes en total. Por favor, elimina algunas imágenes.');
      this.info('Solo puedes guardar hasta 5 imágenes en total. Por favor, elimina algunas imágenes.', '', 'Aceptar', true);
      return;
    }

    this.isLoading = true;

    try {
      // Incluir todas las imágenes como nuevas, independientemente de si tienen archivo asociado
      const newImages: VisionboardImage[] = [];
      for (const image of this.images) {
        newImages.push({
          id: image.imageId,
          imageData: image.preview
        });
      }

      // Verificar límite de imágenes nuevas
      if (newImages.length > 5) {
        // alert('Solo puedes agregar hasta 5 imágenes nuevas en total.');
        this.info('Solo puedes agregar hasta 5 imágenes nuevas en total.', '', 'Aceptar', true);
        this.isLoading = false;
        return;
      }

      const visionboardData: VisionboardDto = {
        id: this.visionboardId || undefined,
        name: this.visionboardName,
        description: this.visionboardDescription || '',
        images: this.images.map(img => ({ imageId: img.imageId })),
        newImages: newImages
      };

      this.visionboardService.saveVisionboard(this.userId, visionboardData).subscribe({
        next: (response) => {
          // alert('Visionboard guardado correctamente');
          this.success('Visionboard guardado correctamente');
          this.isLoading = false;
          this.visionboardSaved.emit();

          if (!visionboardData.id) {
            this.resetEditor();
          } else {
            this.visionboardId = response.data.id;
            // Marcar todas las imágenes como existentes (sin archivo asociado)
            this.imageFiles = this.imageFiles.map(img => ({ ...img, file: null }));
          }
        },
        error: (error) => {
          this.isLoading = false;
          alert('Error al guardar visionboard: ' + (error.error?.message || 'Error desconocido'));
        }
      });
    } catch (error) {
      this.isLoading = false;
      alert('Error al procesar las imágenes');
    }
  }



  // Método para compartir visionboard (reservado para implementación futura)
  shareVisionboard(): void { }

  removeImage(index: number): void {
    const removedImageId = this.images[index]?.imageId;
    this.images.splice(index, 1);

    if (removedImageId) {
      const fileIndex = this.imageFiles.findIndex(imgFile => imgFile.id === removedImageId);
      if (fileIndex >= 0) {
        this.imageFiles.splice(fileIndex, 1);
      }
    }
  }

  drop(event: CdkDragDrop<any[]>) {
    moveItemInArray(this.images, event.previousIndex, event.currentIndex);
  }

  trackByImageId(index: number, image: any): string {
    return image.imageId;
  }
} 